import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Toujours activés
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait un choix
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Afficher le bandeau après un court délai
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setShowBanner(false);
    // console.log('❌ Cookies non essentiels refusés');
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowSettings(false);
    // console.log('⚙️ Préférences enregistrées:', preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Les cookies nécessaires ne peuvent pas être désactivés
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-96 z-50"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Bandeau principal minimal */}
            {!showSettings ? (
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  {/* Icône minimaliste */}
                  <div className="flex-shrink-0 w-10 h-10 bg-[#00BFA5]/10 rounded-xl flex items-center justify-center">
                    <Cookie className="w-5 h-5 text-[#00BFA5]" />
                  </div>

                  {/* Contenu simplifié */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      🍪 Nous utilisons des cookies
                    </h3>
                    <p className="text-sm text-gray-600">
                      Nous utilisons des cookies pour améliorer votre expérience sur notre site, analyser le trafic et personnaliser le contenu. Vous pouvez accepter tous les cookies, les refuser ou personnaliser vos préférences.
                    </p>
                  </div>

                  {/* Bouton fermer discret */}
                  <button
                    onClick={handleRejectAll}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Boutons d'action minimalistes */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleAcceptAll}
                    className="flex-1 bg-[#00BFA5] hover:bg-[#00A68F] text-white font-semibold text-sm h-10 rounded-xl"
                  >
                    Accepter tout
                  </Button>
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    className="text-gray-600 hover:bg-gray-100 font-medium text-sm h-10 rounded-xl px-4"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Personnaliser
                  </Button>
                </div>

                {/* Lien politique de confidentialité discret */}
                <p className="text-xs text-gray-400 mt-3 text-center">
                  En savoir plus sur notre{' '}
                  <a 
                    href="/politique-confidentialite" 
                    className="text-[#00BFA5] hover:text-[#00A68F] underline"
                  >
                    politique de confidentialité
                  </a>
                </p>
              </div>
            ) : (
              /* Panneau de personnalisation simplifié */
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#00BFA5]/10 rounded-lg flex items-center justify-center">
                      <Settings className="w-4 h-4 text-[#00BFA5]" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900">
                      Gérer les cookies
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Options de cookies simplifiées */}
                <div className="space-y-3 mb-4">
                  {/* Cookies nécessaires */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Shield className="w-3 h-3 text-green-600" />
                        <h4 className="text-sm font-semibold text-gray-900">Nécessaires</h4>
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                          Requis
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Essentiels au fonctionnement
                      </p>
                    </div>
                    <div className="w-10 h-5 bg-green-600 rounded-full flex items-center justify-end px-0.5">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>

                  {/* Cookies analytiques */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Analytiques</h4>
                      <p className="text-xs text-gray-500">
                        Mesure d'audience
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-10 h-5 rounded-full flex items-center transition-all ${
                        preferences.analytics
                          ? 'bg-[#00BFA5] justify-end'
                          : 'bg-gray-300 justify-start'
                      } px-0.5`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>

                  {/* Cookies marketing */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-0.5">Marketing</h4>
                      <p className="text-xs text-gray-500">
                        Publicités personnalisées
                      </p>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-10 h-5 rounded-full flex items-center transition-all ${
                        preferences.marketing
                          ? 'bg-[#00BFA5] justify-end'
                          : 'bg-gray-300 justify-start'
                      } px-0.5`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleSavePreferences}
                    className="flex-1 bg-[#00BFA5] hover:bg-[#00A68F] text-white font-semibold text-sm h-10 rounded-xl"
                  >
                    Enregistrer
                  </Button>
                  <Button
                    onClick={() => setShowSettings(false)}
                    variant="outline"
                    className="border border-gray-300 hover:bg-gray-50 font-medium text-sm h-10 rounded-xl px-4"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
